import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSessionStorage, useUnmount } from 'usehooks-ts';
import { ApplicationState, CommanderThunkDispatch, WithGuideLink, WithReservationId } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseReservationGroupId } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector } from '@mews-commander/utils';
import { initializeReservationModule } from '../../actions';
import { ModalBody } from '../../../modalManager/ModalBody';
import { getCheckedReservationIds } from '../../selectors';
import { subscribeResourceUpdates, unsubscribeResourceUpdates } from '../../../../core/websockets/eventHandlers/spaces';
import { useHasNavigatedBack } from '../../../../core/history';
import { useLoaderVisible } from '../../../../core/pageLoader';
import ReservationModule from './ReservationModule';
import { FetchingContextProvider } from './context';

export const ReservationDetailContainer = ({ reservationId, guideLink }: WithReservationId & WithGuideLink) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const [fetching, setFetching] = useState(false);

    /**
     * At this moment, the groupId must be guaranteed to exist. It's prefetched on route level, there's no way it can be undefined.
     */
    const groupId = useSelector((state: ApplicationState) =>
        getCurrentEnterpriseReservationGroupId(state, { id: reservationId })
    ) as string;
    const enterpriseId = useCurrentEnterpriseId();

    const hasNavigatedBack = useHasNavigatedBack();
    const [storedReservationIds, storeReservationIds] = useSessionStorage(`RM-group-reservations-${groupId}`, [
        reservationId,
    ]);
    const reservationIds = useCommanderSelector((state) => getCheckedReservationIds(state, { scope: groupId }));

    const [isActive, setActive] = useState(false);
    const isWindowFetching = useLoaderVisible();

    const isFetching = isWindowFetching || fetching;

    useEffect(() => {
        dispatch(subscribeResourceUpdates());
        return () => {
            dispatch(unsubscribeResourceUpdates());
        };
    }, [dispatch]);

    useEffect(() => {
        if (groupId) {
            if (!isActive) {
                void dispatch(
                    initializeReservationModule({
                        enterpriseId,
                        groupId,
                        reservationIds: hasNavigatedBack ? storedReservationIds : [reservationId],
                    })
                );
                setActive(true);
            }
        }
    }, [dispatch, enterpriseId, groupId, reservationId, storedReservationIds, hasNavigatedBack, isActive]);

    useUnmount(() => {
        storeReservationIds(reservationIds);
    });

    if (!groupId) return null;

    return (
        <FetchingContextProvider value={{ setFetching }}>
            <ModalBody isActive={isActive} isFetching={isFetching}>
                <ReservationModule
                    enterpriseId={enterpriseId}
                    groupId={groupId}
                    reservationId={reservationId}
                    guideLink={guideLink}
                />
            </ModalBody>
        </FetchingContextProvider>
    );
};
